import { DataTable } from '@/components/table/DataTable';
import { Box } from '@/components/ui/box';
import { Button } from '@/components/ui/button';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { MarkdownText } from '@/shared/MarkdownText';
import { UITools } from '@ditto/shared';
import { ColumnDef, getPaginationRowModel } from '@tanstack/react-table';
import {
  ChevronDown,
  ChevronUp,
  CornerDownRight,
  ExternalLink,
} from 'lucide-react';
import { FC, useState } from 'react';
import { generateRegulationLink } from './utils';

export type FDAAdverseEventsSearchToolInput =
  UITools['fdaAdverseEventsSearch']['input'];
export type FDAAdverseEventsSearchToolOutput =
  UITools['fdaAdverseEventsSearch']['output'];

export const FDAAdverseEventsSearchDisplay: FC<{
  input: FDAAdverseEventsSearchToolInput;
  output: FDAAdverseEventsSearchToolOutput;
}> = ({ input, output }) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Build markdown text for input parameters
  const markdownText = [
    input?.device_name ? `- Device Name: ${input.device_name}` : null,
    input?.brand_name ? `- Brand Name: ${input.brand_name}` : null,
    input?.manufacturer_name
      ? `- Manufacturer: ${input.manufacturer_name}`
      : null,
    input?.start_date ? `- Start Date: ${formatDate(input.start_date)}` : null,
    input?.end_date ? `- End Date: ${formatDate(input.end_date)}` : null,
    input?.event_type ? `- Event Type: ${input.event_type}` : null,
    input?.product_code ? `- Product Code: ${input.product_code}` : null,
    input?.device_class ? `- Device Class: ${input.device_class}` : null,
    input?.limit && input.search_type === 'records'
      ? `- Limit: ${input.limit}`
      : null,
  ]
    .filter(Boolean)
    .join('\n');

  const searchUrl =
    'https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfmaude/search.cfm';

  const isCountSearch = input.search_type === 'count';
  const hasResults = isCountSearch
    ? output.count_results && output.count_results.length > 0
    : output.results && output.results.length > 0;

  return (
    <FlexRow alignItems="stretch" w="full" gap="4">
      <FlexCol gap="4">
        <FlexCol gap="2">
          <a href={searchUrl} target="_blank" rel="noopener noreferrer">
            <FlexRow gap="1">
              <Text variant="body" className="text-neutral-600">
                Search in
              </Text>
              <Text variant="body" className="text-neutral-600 underline">
                FDA Adverse Events Database
              </Text>
              <Text variant="body" className="text-neutral-600">
                for:
              </Text>
            </FlexRow>
          </a>
          <MarkdownText>{markdownText}</MarkdownText>

          {/* Show metadata */}
          {output.meta && (
            <FlexRow gap="2" className="text-sm text-neutral-500">
              <Text variant="body-sm">
                Total found: {output.meta.total?.toLocaleString() || 0}
              </Text>
              {output.meta.limit && (
                <Text variant="body-sm">
                  • Showing:{' '}
                  {Math.min(output.meta.limit, output.meta.total || 0)}
                </Text>
              )}
            </FlexRow>
          )}
        </FlexCol>

        {hasResults ? (
          <div className="overflow-y-auto max-w-[800px]">
            {isCountSearch ? (
              <DataTable
                columns={countColumns}
                data={output.count_results!}
                options={{
                  getPaginationRowModel: getPaginationRowModel(),
                  onPaginationChange: setPagination,
                  state: { pagination },
                }}
              />
            ) : (
              <DataTable
                columns={recordsColumns}
                data={output.results!}
                getRowCanExpand={() => true}
                renderSubComponent={row => (
                  <AdditionalDetails event={row.original} />
                )}
                options={{
                  getPaginationRowModel: getPaginationRowModel(),
                  onPaginationChange: setPagination,
                  state: { pagination },
                }}
              />
            )}
          </div>
        ) : (
          <FlexCol gap="2">
            <Text variant="body" className="text-neutral-600">
              No adverse events found.
            </Text>
          </FlexCol>
        )}
      </FlexCol>
    </FlexRow>
  );
};

// Columns for count results
const countColumns: ColumnDef<{ term: string; count: number }>[] = [
  {
    accessorKey: 'term',
    header: () => (
      <Text variant="body" color="primary">
        Term
      </Text>
    ),
    cell: ({ row }) => (
      <Text variant="body" className="text-neutral-600">
        {row.original.term}
      </Text>
    ),
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[200px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[200px]',
    },
  },
  {
    accessorKey: 'count',
    header: () => (
      <Text variant="body" color="primary">
        Count
      </Text>
    ),
    cell: ({ row }) => (
      <Text variant="body" className="text-neutral-600 font-mono">
        {row.original.count.toLocaleString()}
      </Text>
    ),
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[100px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[100px]',
    },
  },
];

// Helper function to format YYYYMMDD to readable date
const formatDate = (dateStr: string | undefined) => {
  if (!dateStr || dateStr.length !== 8) return '—';

  try {
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);
    const day = dateStr.slice(6, 8);
    const date = new Date(`${year}-${month}-${day}`);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

// Columns for adverse event records
// Order: generic name > manufacturer > brand name > date of report > product code > problem name > link
const recordsColumns: ColumnDef<
  NonNullable<FDAAdverseEventsSearchToolOutput['results']>[number]
>[] = [
  {
    id: 'expander',
    header: () => null,
    cell: ({ row }) => (
      <Button
        variant="ghost"
        size="sm"
        onClick={e => {
          e.stopPropagation();
          row.toggleExpanded();
        }}
        className="p-0 h-8 w-8"
      >
        {row.getIsExpanded() ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </Button>
    ),
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[50px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[50px]',
    },
  },
  {
    accessorKey: 'device_generic_name',
    header: () => (
      <Text variant="body" color="primary">
        Device Generic Name
      </Text>
    ),
    cell: ({ row }) => {
      const genericName = row.original.device?.generic_name || '—';
      return (
        <Text variant="body" className="text-neutral-600">
          {genericName}
        </Text>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[200px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[200px]',
    },
  },
  {
    accessorKey: 'manufacturer_name',
    header: () => (
      <Text variant="body" color="primary">
        Manufacturer
      </Text>
    ),
    cell: ({ row }) => {
      const manufacturer = row.original.manufacturer_name || '—';
      return (
        <Text variant="body" className="text-neutral-600">
          {manufacturer}
        </Text>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[150px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[150px]',
    },
  },
  {
    accessorKey: 'brand_name',
    header: () => (
      <Text variant="body" color="primary">
        Brand Name
      </Text>
    ),
    cell: ({ row }) => {
      const brandName = row.original.device?.brand_name || '—';
      return (
        <Text variant="body" className="text-neutral-600">
          {brandName}
        </Text>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[150px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[150px]',
    },
  },
  {
    accessorKey: 'date_received',
    header: () => (
      <Text variant="body" color="primary">
        Date of Report
      </Text>
    ),
    cell: ({ row }) => {
      const dateStr = row.original.date_received;
      const formattedDate = formatDate(dateStr);
      return (
        <Text variant="body" className="text-neutral-600">
          {formattedDate}
        </Text>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[120px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[120px]',
    },
  },
  {
    accessorKey: 'product_code',
    header: () => (
      <Text variant="body" color="primary">
        Product Code
      </Text>
    ),
    cell: ({ row }) => {
      const productCode = row.original.device?.product_code || '—';
      return (
        <Text variant="body" className="text-neutral-600 font-mono">
          {productCode}
        </Text>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[100px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[100px]',
    },
  },
  {
    accessorKey: 'product_problems',
    header: () => (
      <Text variant="body" color="primary">
        Problem Name
      </Text>
    ),
    cell: ({ row }) => {
      const problems = row.original.product_problems;
      const problemText = problems && problems.length > 0 ? problems[0] : '—';
      return (
        <Text variant="body" className="text-neutral-600">
          {problemText}
        </Text>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[150px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[150px]',
    },
  },
  {
    accessorKey: 'fda_link',
    header: () => (
      <Text variant="body" color="primary">
        Link
      </Text>
    ),
    cell: ({ row }) => {
      const reportNumber = row.original.report_number;
      if (!reportNumber) {
        return (
          <Text variant="body" className="text-neutral-600">
            —
          </Text>
        );
      }

      const fdaUrl = `https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfmaude/detail.cfm?mdrfoi__id=${row.original.mdr_report_key}`;
      return (
        <a
          href={fdaUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-underline"
        >
          <ExternalLink size={20} />
        </a>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[120px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[120px]',
    },
  },
];

const AdditionalDetails: FC<{
  event: NonNullable<FDAAdverseEventsSearchToolOutput['results']>[number];
}> = ({ event }) => {
  const device = event.device;

  return (
    <FlexCol gap="2">
      <FlexRow gap="2" alignItems="center">
        <CornerDownRight size={16} />
        <Text variant="body-sm" color="primary" className="font-bold">
          Details:
        </Text>
      </FlexRow>
      <Box className="grid grid-cols-2 gap-2">
        {device?.brand_name && (
          <FlexRow gap="1">
            <Text variant="body-sm" className="text-neutral-600 font-bold">
              · Brand Name:
            </Text>
            <Text variant="body-sm" className="text-neutral-600">
              {device.brand_name}
            </Text>
          </FlexRow>
        )}

        {event.patient_problems && event.patient_problems.length > 0 && (
          <FlexCol gap="1" className="col-span-2">
            <Text variant="body-sm" className="text-neutral-600 font-bold">
              · Patient Problems:
            </Text>
            <Text variant="body-sm" className="text-neutral-600">
              {event.patient_problems.join(', ')}
            </Text>
          </FlexCol>
        )}

        {event.narratives && event.narratives.length > 0 && (
          <FlexCol gap="1" className="col-span-2">
            <Text variant="body-sm" className="text-neutral-600 font-bold">
              · Event description:
            </Text>
            <Text variant="body-sm" className="text-neutral-600">
              {event.narratives.join(' | ')}
            </Text>
          </FlexCol>
        )}

        {device?.device_class && (
          <FlexRow gap="1">
            <Text variant="body-sm" className="text-neutral-600 font-bold">
              · Device Class:
            </Text>
            <Text variant="body-sm" className="text-neutral-600">
              Class {device.device_class}
              {device.device_class === '1' && ' (Low Risk)'}
              {device.device_class === '2' && ' (Moderate Risk)'}
              {device.device_class === '3' && ' (High Risk)'}
            </Text>
          </FlexRow>
        )}

        {device?.device_name && (
          <FlexRow gap="1">
            <Text variant="body-sm" className="text-neutral-600 font-bold">
              · Device Name:
            </Text>
            <Text variant="body-sm" className="text-neutral-600">
              {device.device_name}
            </Text>
          </FlexRow>
        )}

        {device?.regulation_number && (
          <FlexRow gap="1">
            <Text variant="body-sm" className="text-neutral-600 font-bold">
              · Regulation Number:
            </Text>
            {generateRegulationLink(device.regulation_number) ? (
              <a
                href={generateRegulationLink(device.regulation_number)!}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline text-sm"
              >
                {device.regulation_number}
              </a>
            ) : (
              <Text variant="body-sm" className="text-neutral-600">
                {device.regulation_number}
              </Text>
            )}
          </FlexRow>
        )}
      </Box>
    </FlexCol>
  );
};
